import {
  Button,
  Close,
  Divider,
  EButtonColorVariant,
  EDividerVariant,
  EHeadingStyleVariant,
  Heading,
  Textarea,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import QRCode from 'react-qr-code';
import useSWR from 'swr';

import Modal from '@/components/switchback/Modal';
import { emailValidator } from '@/constants/validationRules';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { EToastVariant, useToast } from '@/hooks/useToast';
import { trackReferralCodeSharedEvent } from '@/services/analytics/referral';
import apiRequest from '@/services/apiRequest';
import { sendReferrals } from '@/services/getReferralData';
import { EShareMethodType, IReferralStatsProps } from '@/services/types/referral/referral';
import { getCoreApi } from '@/utility/getCoreApi';

type TShareModalProps = {
  shareUrl: string;
  isOpened: boolean;
  onClose: () => void;
};

const getValidEmails = (emails: string) =>
  emails
    .split(',')
    .map(possibleEmail => {
      const email = possibleEmail.trim();
      return email.match(emailValidator.pattern) ? { email } : undefined;
    })
    .filter(Boolean) as Array<{ email: string }>;

export const ShareModal = ({ shareUrl, isOpened, onClose }: TShareModalProps) => {
  const intl = useIntl();
  const { showToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [emails, setEmails] = useState('');
  const [sendingReferrals, setSendingReferrals] = useState(false);

  const { data: referralStats, mutate: updateReferralStats } = useSWR(
    `${getCoreApi()}/referrals/stats`,
    url => apiRequest<IReferralStatsProps>({ url }, true),
  );

  const shareToEmails = async () => {
    const validEmails = getValidEmails(emails);

    if (!validEmails.length) {
      return showToast(
        EToastVariant.Error,
        intl.formatMessage({ defaultMessage: 'Invalid email addresses', id: 'eJSrNF' }),
        intl.formatMessage({
          defaultMessage: 'Please insert valid email addresses.',
          id: 'pS1jck',
        }),
      );
    }

    try {
      setSendingReferrals(true);

      const { referred } = await sendReferrals({
        referral_type: 'new_booking',
        referee_type: 'renter',
        referees: validEmails,
      });

      if (Array.isArray(referred) && referred.length) {
        showToast(
          EToastVariant.Success,
          intl.formatMessage({ defaultMessage: 'Emails sent!', id: '7+Dwqd' }),
          intl.formatMessage({ defaultMessage: 'Successfully sent the emails.', id: 'lExkgO' }),
        );
      } else {
        showToast(
          EToastVariant.Alert,
          intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
          intl.formatMessage({
            defaultMessage: 'It looks like you already invited your friends.',
            id: 'oTcN60',
          }),
        );
      }

      onClose();
      setEmails('');

      const updatedReferralStats = await updateReferralStats();

      trackReferralCodeSharedEvent({
        shareMethod: EShareMethodType.EMAIL,
        referralType: 'renter',
        currentEarningsEarned: (updatedReferralStats?.earned_credits || 0) / 100,
        currentEarningsPending: (updatedReferralStats?.pending_credits || 0) / 100,
      });
    } catch {
      showToast(
        EToastVariant.Error,
        intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
        intl.formatMessage({
          defaultMessage: 'Something went wrong, please try again.',
          id: '8GvVWZ',
        }),
      );
    } finally {
      setSendingReferrals(false);
    }
  };

  const handleCloseModal = () => {
    // Assume that most likely the user shared via QR code if they close the modal.
    trackReferralCodeSharedEvent({
      shareMethod: EShareMethodType.QR_LIKELY,
      referralType: 'renter',
      currentEarningsEarned: (referralStats?.earned_credits || 0) / 100,
      currentEarningsPending: (referralStats?.pending_credits || 0) / 100,
    });

    onClose();
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: intl.formatMessage({
          defaultMessage: `Give $50. Get $75. It’s a win-win!`,
          id: 'LqvprS',
        }),
        text: intl.formatMessage({
          defaultMessage: `Help friends save on trips and earn credits for your own adventures.`,
          id: 'u+bnGG',
        }),
        url: shareUrl,
      });

      trackReferralCodeSharedEvent({
        shareMethod: EShareMethodType.SHARE_SHEET,
        referralType: 'renter',
        currentEarningsEarned: (referralStats?.earned_credits || 0) / 100,
        currentEarningsPending: (referralStats?.pending_credits || 0) / 100,
      });
    } catch {
      // Cancelling native share modal also throws an error.
      // Since we can't determine what is the actual error, ignore it.
    }
  };

  return (
    // Bonfire modal has issues on mobile when opening keyboard. Use switchback modal for now.
    <Modal open={isOpened} onClose={handleCloseModal} disabledManualClose>
      <div className="absolute top-0 right-0 m-2">
        <Close onClose={handleCloseModal} />
      </div>

      <div className="flex flex-col md:flex-row">
        <div className="flex flex-col justify-center w-full text-center gap-6">
          <Heading variant={EHeadingStyleVariant.H6}>
            {intl.formatMessage({
              defaultMessage: 'Scan me for $50 off your next Outdoorsy trip',
              id: 'Rkrw8r',
            })}
          </Heading>

          <div className="flex justify-center">
            <QRCode value={shareUrl} />
          </div>
        </div>

        <div className="my-10 md:my-0 md:mx-10">
          <Divider variant={isMobile ? EDividerVariant.Horizontal : EDividerVariant.Vertical} />
        </div>

        <div className="flex flex-col justify-center w-full text-center gap-6">
          <Heading variant={EHeadingStyleVariant.H6}>
            {intl.formatMessage({ defaultMessage: 'Refer by email', id: 'sExqdh' })}
          </Heading>

          <div className="text-left">
            <Textarea
              controlled
              inputValue={emails}
              onChange={setEmails}
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Enter email address',
                id: 'MnA9KQ',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter email address',
                id: 'MnA9KQ',
              })}
              helpText={intl.formatMessage({
                defaultMessage: 'You can add multiple email address separated by a comma.',
                id: '8R9tQh',
              })}
              className="md:min-h-[100px]"
            />
          </div>

          <Button
            variant={EButtonColorVariant.Primary}
            label={intl.formatMessage({ defaultMessage: 'Send', id: '9WRlF4' })}
            onClick={() => shareToEmails()}
            loading={sendingReferrals}
            disabled={!emails.length}
          />

          {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
            <Button
              variant={EButtonColorVariant.Secondary}
              label={intl.formatMessage({ defaultMessage: 'More ways to share', id: 'ybCFFe' })}
              onClick={handleNativeShare}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
